import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Mail,
  Save,
  RotateCcw,
  FileDown,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";
import { Section, DraftDisclaimer } from "./ReportPrimitives";
import { buildReportPdf } from "@/lib/reports/pdfBase";
import PolishPanel from "./PolishPanel";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Shared "report shell" — renders the title block, top disclaimer, action bar,
// review-checklist card, the section accordions (provided by caller), and the
// live polished preview + footer disclaimer. Each page only supplies content.
export default function ReportShell({
  testIdPrefix,        // e.g. "dmr"
  title,
  subtitle,
  brandLine,           // brand line for the PDF header (e.g. "Daily Mud Report")
  emptyState,
  buildFn,             // (state) -> polished plain-text body
  checklistFn,         // (state) -> string[]
  store,               // makeReportStorage handle
  sections,            // array of { id, badge, title, render(state, set), defaultOpen }
  quickPhraseAppend,   // (state, set, phrase) -> mutate via set; called by QuickPhrase chips inside sections (optional)
  filenameFromState,
  topNotice,           // optional ReactNode rendered ABOVE the actions bar (e.g. HSE regulatory reminder)
  bottomNotice,        // optional ReactNode rendered BELOW the preview (e.g. HSE regulatory reminder)
  reportType,          // identifier for the Polish backend (e.g. "kickSheet", "dailyOperationsSummary")
  safetyCritical,      // boolean — applies restrictive polishing prompt + post-scrub
  // RCE-3 opt-in hooks — every prop is optional, default behaviour unchanged.
  initialState,        // optional pre-populated state (callers can pass an
                       // RCE-inherited state on mount)
  onAfterSave,         // (state, savedItem) — called after the legacy store.save
                       // succeeds. Used by DMR to persist a parallel Daily Fluid
                       // Doc with a frozen context snapshot.
  rceContextStrip,     // optional ReactNode rendered above the action bar
  rceExtraAction,      // optional ReactNode rendered alongside the action bar
                       // (e.g. "Re-pull from Context" button)
}) {
  const [state, setState] = useState(() => {
    const base = emptyState();
    return initialState ? { ...base, ...initialState } : base;
  });
  const setOpenMap = sections.reduce((acc, s) => ({ ...acc, [s.id]: !!s.defaultOpen }), {});
  const [open, setOpen] = useState(setOpenMap);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const set = (group, key, value) =>
    setState((s) => ({ ...s, [group]: { ...s[group], [key]: value } }));

  const issues = useMemo(() => checklistFn(state), [state, checklistFn]);
  const polished = useMemo(() => buildFn(state), [state, buildFn]);

  const onSave = () => {
    // RCE persistence runs FIRST and is independent of the legacy report
    // store paywall (RCE is foundational and never gated).
    if (typeof onAfterSave === "function") {
      try { onAfterSave(state, null); }
      catch (e) { console.warn("[ReportShell onAfterSave hook]", e); }
    }
    const item = store.save(state);
    setState((s) => ({ ...s, id: item.id }));
    toast.success("Draft saved locally");
  };
  const onReset = () => {
    if (window.confirm("Reset the entire form? Unsaved input will be lost.")) {
      setState(emptyState());
      toast("Form reset");
    }
  };
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(polished); toast.success("Report copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };
  const onEmail = () => {
    const subject = title;
    const body = polished.slice(0, 1800);
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const onPdf = () => {
    try {
      buildReportPdf({
        title,
        subtitle: subtitle || "",
        brandLine,
        body: polished,
        filename: filenameFromState ? filenameFromState(state) : `RigSentinel_${testIdPrefix}_${Date.now()}.pdf`,
      });
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  // Shared helper used by PolishPanel to export a polished/translated body
  // via the existing PDF chrome. Keeps the deterministic state untouched.
  const exportPolishedPdf = (polishedBody, suffix = "Polished") => {
    try {
      buildReportPdf({
        title: `${title} — ${suffix === "Polished" ? "Professionally Polished Draft" : "Professionally Polished English Draft"}`,
        subtitle: subtitle || "",
        brandLine,
        body: polishedBody,
        filename: (filenameFromState ? filenameFromState(state) : `RigSentinel_${testIdPrefix}_${Date.now()}`).replace(/\.pdf$/i, "") + `_${suffix}.pdf`,
      });
    } catch (e) {
      throw e;
    }
  };

  return (
    <div className="flex flex-col gap-4 bg-grain pb-2" data-testid={`${testIdPrefix}-page`}>
      <section className="pt-1">
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">
          Documentation · {testIdPrefix.toUpperCase()}
        </div>
        <h1 className="font-heading font-black text-3xl tracking-tight mt-1" data-testid={`${testIdPrefix}-title`}>
          {title}
        </h1>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">
          Write what happened in simple words. RigSentinel cleans the wording into a professional report. Missing fields are marked as <em>Not entered</em>.
        </p>
      </section>

      <DraftDisclaimer testId={`${testIdPrefix}-disclaimer`} />

      {topNotice && (
        <div data-testid={`${testIdPrefix}-top-notice`}>{topNotice}</div>
      )}

      {rceContextStrip && (
        <div data-testid={`${testIdPrefix}-rce-context`}>{rceContextStrip}</div>
      )}

      <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-actions`}>
        <button data-testid={`${testIdPrefix}-save-btn`} onClick={onSave} className="btn-amber h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> Save Draft
        </button>
        {rceExtraAction}
        <button data-testid={`${testIdPrefix}-pdf-btn`} onClick={onPdf} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <FileDown className="w-3.5 h-3.5" /> Export PDF
        </button>
        <button data-testid={`${testIdPrefix}-copy-btn`} onClick={onCopy} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
        <button data-testid={`${testIdPrefix}-email-btn`} onClick={onEmail} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button data-testid={`${testIdPrefix}-reset-btn`} onClick={onReset} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </button>
      </div>

      <div
        data-testid={`${testIdPrefix}-review-checklist`}
        className="card-rs p-3"
        style={{
          background: issues.length ? "rgba(244,67,54,0.06)" : "rgba(76,175,80,0.06)",
          borderColor: issues.length ? "#C62828" : "rgba(76,175,80,0.5)",
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          {issues.length ? (
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: "var(--rs-danger)" }} />
          ) : (
            <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#4CAF50" }} />
          )}
          <span className="text-[12px] font-semibold" style={{ color: issues.length ? "var(--rs-danger)" : "#86EFAC" }}>
            {issues.length ? `Review checklist — ${issues.length} item(s) need attention` : "Review checklist clean — all critical fields entered"}
          </span>
        </div>
        {issues.length > 0 && (
          <ul className="text-[11px] text-[var(--rs-text-2)] list-disc pl-4 leading-snug" data-testid={`${testIdPrefix}-review-issues`}>
            {issues.map((i, idx) => <li key={idx}>{i}</li>)}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3">
        {sections.map((s) => (
          <Section
            key={s.id}
            id={`${testIdPrefix}-${s.id}`}
            badge={s.badge}
            title={s.title}
            open={open[s.id]}
            onToggle={() => toggle(s.id)}
          >
            {s.render(state, set, { quickPhraseAppend, setState })}
          </Section>
        ))}
      </div>

      <section className="card-rs p-3.5" data-testid={`${testIdPrefix}-preview-card`}>
        <div className="flex items-center justify-between mb-2">
          <div className="label-up text-[var(--rs-amber)]">Polished Report Preview</div>
          <div className="text-[10px] text-[var(--rs-text-3)]">Updates live as you type</div>
        </div>
        <pre
          data-testid={`${testIdPrefix}-preview-text`}
          className="text-[11px] leading-snug whitespace-pre-wrap text-[var(--rs-text-2)]"
          style={{
            background: "rgba(10,11,14,0.6)",
            padding: 12,
            borderRadius: 6,
            maxHeight: 480,
            overflowY: "auto",
            fontFamily: "JetBrains Mono, ui-monospace, monospace",
          }}
        >
{polished}
        </pre>
      </section>

      {reportType && (
        <section data-testid={`${testIdPrefix}-polish-section`} className="flex flex-col gap-2">
          <PolishPanel
            testIdPrefix={testIdPrefix}
            reportType={reportType}
            reportTitle={title}
            safetyCritical={safetyCritical}
            structuredData={state}
            roughNotes={(state.notes && state.notes.roughNotes) || ""}
            deterministicDraft={polished}
            onExportPdf={(polishedBody) => exportPolishedPdf(polishedBody, "Polished")}
            onExportTranslatedPdf={(translatedBody) => exportPolishedPdf(translatedBody, "Polished_English")}
          />
        </section>
      )}

      <div className="card-rs p-3" data-testid={`${testIdPrefix}-footer-disclaimer`} style={{ background: "rgba(22,24,29,0.65)", borderColor: "var(--rs-border)" }}>
        <div className="flex items-start gap-2.5">
          <ShieldAlert className="w-3.5 h-3.5 mt-[3px] shrink-0" style={{ color: "var(--rs-text-3)" }} />
          <p className="text-[11px] leading-snug text-[var(--rs-text-3)]">
            <span className="font-semibold" style={{ color: "var(--rs-text-2)" }}>Documentation tool.</span> Output is a draft. Review, correct, and approve under company procedures before submission.
          </p>
        </div>
      </div>

      {bottomNotice && (
        <div data-testid={`${testIdPrefix}-bottom-notice`}>{bottomNotice}</div>
      )}
    </div>
  );
}
